import { useState, useCallback } from 'react'
import { apiClient } from '@/lib/api/client'
import type { TaxEstimateRequest, TaxEstimateResponse } from '@/lib/api/types'

interface UseTaxEstimateReturn {
  estimate: (request: TaxEstimateRequest) => Promise<TaxEstimateResponse | null>
  loading: boolean
  error: string | null
  data: TaxEstimateResponse | null
  reset: () => void
}

export function useTaxEstimate(): UseTaxEstimateReturn {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [data, setData] = useState<TaxEstimateResponse | null>(null)

  const estimate = useCallback(async (request: TaxEstimateRequest) => {
    setLoading(true)
    setError(null)
    setData(null)

    try {
      const result = await apiClient.estimateTax(request)
      setData(result)
      return result
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to estimate tax'
      setError(errorMessage)
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const reset = useCallback(() => {
    setLoading(false)
    setError(null)
    setData(null)
  }, [])

  return { estimate, loading, error, data, reset }
}
